import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ADDITIONAL_ROOM_OPTIONS,
  ADDITIONAL_SERVICE_OPTIONS,
  AVAILABILITY_OPTIONS,
  BATHROOM_COUNT_OPTIONS,
  BLINDS_OPTIONS,
  COUNTRY_OPTIONS,
  FLOOR_COVERING_OPTIONS,
  FLOOR_OPTIONS,
  OBJECT_TYPE_OPTIONS,
  ROOM_OPTIONS,
} from '@webapp/core/constants/inquiry-labels';
import { InquiryServiceType } from '@webapp/core/enums';
import { IInquiry } from '@webapp/core/interfaces';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { PublicInquiryManagerService } from 'projects/webapp/app/services/public-inquiry-manager.service';
import { PublicInquiryService } from 'projects/webapp/app/services/public-inquiry.service';
import { futureDate, nextDate } from 'projects/webapp/app/utils/validators';
import { Subject, interval, takeUntil } from 'rxjs';

declare const gtag: (...args: any[]) => void;

/** The blocks of the summary. Only one is ever in edit mode. */
export type SummarySection = 'from' | 'to' | 'services' | 'cleaning' | 'contact';

@Component({
  selector: 'app-inquiry-summary',
  templateUrl: './step_summary.component.html',
  // The controls (vo-input, vo-chip, …) come from the global
  // styles/inquiry.scss, like the wizard's; this sheet only lays out the rows.
  styleUrls: ['./step_summary.component.scss']
})
export class InquirySummaryComponent implements OnInit, OnDestroy {
  /**
   * What we show. Normally the API's answer; after the window closed it falls
   * back to the locally collected steps so the customer still sees their data.
   */
  inquiry: Partial<IInquiry> | null = null;

  isLoading = true;
  isSaving = false;

  /** True once the inquiry is on its way to the partners. Nothing is editable then. */
  isLocked = false;

  editing: SummarySection | null = null;
  remainingSeconds = 0;

  draftInquiryForm = new FormGroup({
    date: new FormControl('', { validators: [Validators.required, futureDate()], nonNullable: true }),
    is_date_flexible: new FormControl(false, { nonNullable: true }),
    handover_date: new FormControl<string | null>(null),

    from_address: new FormControl('', { validators: Validators.required, nonNullable: true }),
    from_zip: new FormControl('', { validators: Validators.required, nonNullable: true }),
    from_city: new FormControl('', { validators: Validators.required, nonNullable: true }),
    from_country: new FormControl('CH', { nonNullable: true }),
    from_object_type: new FormControl('', { validators: Validators.required, nonNullable: true }),
    from_floor: new FormControl('', { nonNullable: true }),
    from_has_lift: new FormControl<boolean | null>(null),

    to_address: new FormControl('', { nonNullable: true }),
    to_zip: new FormControl('', { nonNullable: true }),
    to_city: new FormControl('', { nonNullable: true }),
    to_country: new FormControl('CH', { nonNullable: true }),
    to_object_type: new FormControl('', { nonNullable: true }),
    to_floor: new FormControl('', { nonNullable: true }),
    to_has_lift: new FormControl<boolean | null>(null),

    rooms: new FormControl('', { validators: Validators.required, nonNullable: true }),
    square_meters: new FormControl(
      '',
      { validators: [Validators.required, Validators.min(10), Validators.max(500)], nonNullable: true }
    ),

    selected_additional_services: new FormControl<string[]>([], { nonNullable: true }),
    comment: new FormControl('', { nonNullable: true }),

    floor_coverings: new FormControl<string[]>([], { nonNullable: true }),
    blinds: new FormControl('', { nonNullable: true }),
    additional_rooms: new FormControl<string[]>([], { nonNullable: true }),
    nur_wc: new FormControl('0', { nonNullable: true }),
    dusche_ws: new FormControl('0', { nonNullable: true }),
    bad_wc: new FormControl('0', { nonNullable: true }),
    dusche_bad_wc: new FormControl('0', { nonNullable: true }),

    first_name: new FormControl('', { validators: Validators.required, nonNullable: true }),
    last_name: new FormControl('', { validators: Validators.required, nonNullable: true }),
    phone: new FormControl(
      '',
      { validators: [Validators.required, Validators.pattern(/^[\+]?[0-9\s\-\(\)]{7,20}$/)], nonNullable: true }
    ),
    email: new FormControl('', { validators: [Validators.required, Validators.email], nonNullable: true }),
    availability: new FormControl<string[]>([], { nonNullable: true }),
    newsletter: new FormControl(false, { nonNullable: true }),
  }, { validators: nextDate('date', 'handover_date') });

  objectTypeOptions = OBJECT_TYPE_OPTIONS;
  floorOptions = FLOOR_OPTIONS;
  roomOptions = ROOM_OPTIONS;
  countryOptions = COUNTRY_OPTIONS;
  additionalServices = ADDITIONAL_SERVICE_OPTIONS;
  floorCoveringsOptions = FLOOR_COVERING_OPTIONS;
  blindsOptions = BLINDS_OPTIONS;
  additionalRoomsOptions = ADDITIONAL_ROOM_OPTIONS;
  bathroomOptions = BATHROOM_COUNT_OPTIONS;
  availabilityOptions = AVAILABILITY_OPTIONS;

  get isMove() {
    return this.inquiry?.service === InquiryServiceType.MOVE
      || this.inquiry?.service === InquiryServiceType.MOVE_AND_CLEAN;
  }

  get isClean() {
    return this.inquiry?.service === InquiryServiceType.CLEAN
      || this.inquiry?.service === InquiryServiceType.MOVE_AND_CLEAN;
  }

  get hasChanges() {
    return this.draftInquiryForm.dirty;
  }

  get countdown(): string {
    const minutes = Math.floor(this.remainingSeconds / 60);
    const seconds = this.remainingSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  protected _destroy$ = new Subject<void>();

  constructor(
    protected _toastrService: ToastrService,
    protected _spinnerService: NgxSpinnerService,
    protected _publicInquiryManagerService: PublicInquiryManagerService,
    protected _publicInquiryService: PublicInquiryService,
  ) {
  }

  ngOnInit(): void {
    // This screen is the first one after a successful submission, so the
    // conversion fires here now that the success screen is gone.
    if (typeof gtag === 'function') {
      gtag('event', 'conversion', {
        send_to: 'AW-18194306798/05DXCOzy87QcEO6t3OND',
        value: 10.0,
        currency: 'CHF',
      });
    }

    this._loadInquiry();

    interval(1000)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._tick());
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  hasError(controlName: string, errorName?: string) {
    const control = this.draftInquiryForm.get(controlName);
    const isValid = errorName ? !control?.hasError(errorName) : !control?.invalid;
    return !!control?.touched && !isValid;
  }

  toggleSection(section: SummarySection) {
    if (this.isLocked) return;
    this.editing = this.editing === section ? null : section;
  }

  /** Multi-select fields are plain arrays; the template toggles them by value. */
  isSelected(controlName: 'selected_additional_services' | 'floor_coverings' | 'additional_rooms' | 'availability',
             value: string) {
    return this.draftInquiryForm.controls[controlName].value.includes(value);
  }

  toggleValue(controlName: 'selected_additional_services' | 'floor_coverings' | 'additional_rooms' | 'availability',
              value: string) {
    const control = this.draftInquiryForm.controls[controlName];
    const values = control.value;
    control.setValue(values.includes(value) ? values.filter(v => v !== value) : [...values, value]);
    control.markAsDirty();
  }

  setValue(controlName: 'from_has_lift' | 'to_has_lift', value: boolean) {
    const control = this.draftInquiryForm.controls[controlName];
    control.setValue(value);
    control.markAsDirty();
  }

  onFromAddressPlaceSelected(place: google.maps.places.Place) {
    this._applyPlace(place, 'from');
  }

  onToAddressPlaceSelected(place: google.maps.places.Place) {
    this._applyPlace(place, 'to');
  }

  discardChanges() {
    this.editing = null;
    this._fillForm();
  }

  onSubmit() {
    const uuid = this.inquiry?.uuid;
    if (!uuid || this.isLocked) return;

    this.draftInquiryForm.markAllAsTouched();
    if (!this.draftInquiryForm.valid) {
      this._toastrService.warning('Bitte prüfen Sie die rot markierten Felder.');
      return;
    }

    this.isSaving = true;
    this._spinnerService.show();

    // The API validates the whole inquiry, not just the changed fields, so the
    // untouched values travel along.
    const body = { ...this.inquiry, ...this.draftInquiryForm.value };

    this._publicInquiryService.updateInquiry(uuid, body)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (inquiry) => {
          this.inquiry = inquiry;
          this.editing = null;
          this._fillForm();
          this._toastrService.success('Ihre Angaben wurden aktualisiert.');
        },
        error: (error) => {
          if (error?.status === 410) {
            this._lock();
            return;
          }
          console.error('Error updating inquiry:', error);
          this._toastrService.error('Ups, das hat nicht geklappt. Bitte versuchen Sie es nochmals.');
        }
      })
      .add(() => {
        this.isSaving = false;
        this._spinnerService.hide();
      });
  }

  protected _loadInquiry() {
    const progress = this._publicInquiryManagerService.inquiryCreationProgress;
    const uuid = progress?.uuid;

    if (!uuid) {
      // Nothing to show — the wizard should never route here without a uuid.
      this.isLoading = false;
      this._lock();
      return;
    }

    this._publicInquiryService.getInquiry(uuid)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (inquiry) => {
          this.inquiry = inquiry;
          this._fillForm();
          this._syncCountdown();
        },
        error: () => {
          // 410 (window closed) or a network problem: show what we collected
          // locally rather than an empty screen, read-only.
          this.inquiry = this._publicInquiryManagerService.submittedInquiryData();
          this._lock();
        }
      })
      .add(() => this.isLoading = false);
  }

  protected _fillForm() {
    if (!this.inquiry) return;

    const inquiry = this.inquiry;
    this.draftInquiryForm.reset({
      date: inquiry.date ?? '',
      is_date_flexible: !!inquiry.is_date_flexible,
      handover_date: inquiry.handover_date ?? null,

      from_address: inquiry.from_address ?? '',
      from_zip: inquiry.from_zip ?? '',
      from_city: inquiry.from_city ?? '',
      from_country: inquiry.from_country ?? 'CH',
      from_object_type: inquiry.from_object_type ?? '',
      from_floor: inquiry.from_floor ?? '',
      from_has_lift: inquiry.from_has_lift ?? null,

      to_address: inquiry.to_address ?? '',
      to_zip: inquiry.to_zip ?? '',
      to_city: inquiry.to_city ?? '',
      to_country: inquiry.to_country ?? 'CH',
      to_object_type: inquiry.to_object_type ?? '',
      to_floor: inquiry.to_floor ?? '',
      to_has_lift: inquiry.to_has_lift ?? null,

      rooms: inquiry.rooms ?? '',
      square_meters: inquiry.square_meters ?? '',

      selected_additional_services: [...(inquiry.selected_additional_services ?? [])],
      comment: inquiry.comment ?? '',

      floor_coverings: [...(inquiry.floor_coverings ?? [])],
      blinds: inquiry.blinds ?? '',
      additional_rooms: [...(inquiry.additional_rooms ?? [])],
      nur_wc: inquiry.nur_wc ?? '0',
      dusche_ws: inquiry.dusche_ws ?? '0',
      bad_wc: inquiry.bad_wc ?? '0',
      dusche_bad_wc: inquiry.dusche_bad_wc ?? '0',

      first_name: inquiry.first_name ?? '',
      last_name: inquiry.last_name ?? '',
      phone: inquiry.phone ?? '',
      email: inquiry.email ?? '',
      availability: [...(inquiry.availability ?? [])],
      newsletter: !!inquiry.newsletter,
    });

    this._applyServiceValidators();
  }

  /**
   * A cleaning inquiry has no destination and no lift, a move has no cleaning
   * scope — the same rules the wizard's steps apply.
   */
  protected _applyServiceValidators() {
    const controls = this.draftInquiryForm.controls;
    const moveOnly = [controls.from_floor, controls.to_address, controls.to_zip, controls.to_city,
                      controls.to_object_type, controls.to_floor];

    for (const control of moveOnly) {
      control.setValidators(this.isMove ? Validators.required : null);
      control.updateValueAndValidity({ emitEvent: false });
    }

    for (const control of [controls.from_has_lift, controls.to_has_lift]) {
      control.setValidators(this.isMove ? Validators.required : null);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  protected _syncCountdown() {
    const deadline = this.inquiry?.dispatch_after
      ?? this._publicInquiryManagerService.inquiryCreationProgress?.dispatchAfter;

    if (!deadline) {
      this._lock();
      return;
    }

    const seconds = Math.floor((new Date(deadline).getTime() - Date.now()) / 1000);
    this.remainingSeconds = Math.max(0, seconds);
    if (this.remainingSeconds === 0) this._lock();
  }

  protected _tick() {
    if (this.isLocked || this.isLoading) return;

    if (this.remainingSeconds <= 1) {
      this.remainingSeconds = 0;
      this._lock();
      return;
    }

    this.remainingSeconds -= 1;
  }

  protected _lock() {
    if (this.isLocked) return;

    this.isLocked = true;
    this.editing = null;
    this.remainingSeconds = 0;
    this.draftInquiryForm.disable({ emitEvent: false });
    // The inquiry is final: a later visit to /inquiry should start fresh, but
    // this screen keeps rendering from what it already holds.
    this._publicInquiryManagerService.forgetPersistedProgress();
  }

  protected _applyPlace(place: google.maps.places.Place, prefix: 'from' | 'to') {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const streetNumber = get('street_number');
    const route = get('route');
    const address = [route, streetNumber].filter(Boolean).join(' ');
    const zip = get('postal_code');
    const city = get('locality');

    this.draftInquiryForm.patchValue({
      ...(address ? { [`${prefix}_address`]: address } : {}),
      ...(zip ? { [`${prefix}_zip`]: zip } : {}),
      ...(city ? { [`${prefix}_city`]: city } : {}),
    });
    this.draftInquiryForm.markAsDirty();
  }
}
